import freeMedicine from 'model/actions/city/freeMedicine';
import drunkRob from 'model/actions/city/drunkRob';
import { workWithCallback } from 'model/actions/city/work';
import travelAlone from 'model/actions/travelAlone';
import travelWithCaravan from 'model/actions/travelWithCaravan';
import enableGoal from 'model/actions/city/enableGoal';
import enableShop from 'model/actions/city/enableShop';
import {
  buyAmmo,
  buyAmmoBulk,
  buyFood,
  buyFoodBulk,
  buyHorse,
  buyWater,
  buyWaterBulk
} from 'model/actions/city/merchant';
import leave from 'model/actions/leave';
import nextAction from 'model/actions/utilities/nextAction';
import { randomMessage } from 'model/message.js';

// The narration is the traveler's and stays terse. She is not — she does this every day, she
// has a patter for it, and she is neither kind nor cruel about the money.
//
// Load-bearing: she must name a price. freeMedicine.js calls phases.enableResource(coins) off
// the back of this scene, so her saying the rest of it costs is the only thing that earns the
// coin counter appearing. Rewrite her lines all you like, but leave the price in one of them.
const medicineWoman = onDone => ({
  title: 'the healer',
  description: randomMessage([
    [
      'a woman crosses the road with a bundle under her arm.',
      'she turns the wrist over and looks at what is there.',
      'clean cloth. a cup of something bitter. she waits while it goes down.',
      '"drink the whole of it. it will sit badly for an hour and then it will not."',
      '"the rest of what i carry costs money. that is how i eat."'
    ],
    [
      'she finds me in the shade at the end of the boards.',
      'water first. then the cloth, wound tight enough to hurt.',
      '"too tight is how it ought to feel. leave it alone until morning."',
      '"there is more in the bag. none of it is free but what you have had."'
    ]
  ]),
  buttons: [freeMedicine(onDone)]
});

const firstDrunk = onDone => ({
  title: 'the drunk',
  description: [
    'a staggering drunk trips over me. comes up swearing.',
    'gun out. trigger yanked. click. empty.',
    'he swings wide and the weight of it takes him down.',
    'the gun in the dust. some coins with it.',
    '"after dark this town is nobody\'s."'
  ],
  buttons: [drunkRob(onDone)]
});

// Same man in all four, so the voice has to hold across them: blunt, transactional, hiring off
// the street and not pretending otherwise. The narration stays the traveler's.
//
// The button is workWithCallback, so clicking it *is* the first shift — the player takes the job
// by taking it, with no separate accept step. Each variant therefore has to land three things in
// his mouth: that there is work, that it pays coin, and where and when to be.
const rancher = onDone => ({
  title: 'the rancher',
  description: randomMessage([
    [
      'cattle come up the street and the dust comes with them.',
      'he gets down and looks at my hands and does not ask my name.',
      '"those will do. i have men out there with softer hands than yours."',
      '"east of town, sunup. i pay coin at the end of the day and not before."'
    ],
    [
      'a rider comes in with a dozen head and the street gives way.',
      'hat down. he looks at me a long while from the saddle.',
      '"there is work if you want it, and it pays. it is better than whatever this is."',
      '"past the willows, east. we start when it is light, so do not come at noon telling me you got lost."'
    ],
    [
      'stock in the road. the noise of them fills the town and then it is gone.',
      'he rides the length of the boards and stops where i am sitting.',
      '"i have work and it pays in coin. what you are doing does not, near as i can tell."',
      '"east, past the last of the fences. first light. i will not come looking for you."'
    ],
    [
      'a herd goes by slow and the smell of them stays after.',
      'the man counting them turns and counts me the same way.',
      '"i have fence down and nobody to hold it. it pays the same whether you take to the work or not."',
      '"east. two hours on foot. be there when the sun is up."'
    ]
  ]),
  buttons: [workWithCallback(onDone)]
});

const examineMap = ({ calendar, terrain }, onDone) => ({
  title: 'the map',
  description: [
    'the map goes out across the bar. the edges will not lie flat.',
    `a date circled in red. ${calendar.getEndDate()}.`,
    'i do not know what waits there. i know the date.',
    ...terrain.describeJourney()
  ],
  buttons: [nextAction('Continue', () => onDone(barkeep(onDone)))]
});

const barkeep = onDone => ({
  title: 'the barkeep',
  description: [
    'the barkeep sets a glass down and does not move off. he is smirking. "whatcha pay for that treasure map?"',
    'i put my finger on the marked spot. "i need to get here."',
    'the smirk goes. his eyes drop to my belt and then past me to the door.',
    '"caravan leaves in that direction soon." he waits.',
    '"or are you going alone?"',
    'only bandits and outlaws travel alone.'
  ],
  buttons: [travelAlone(() => onDone(barkeepAlone(onDone))), travelWithCaravan(onDone)]
});

/**
 * The one button that closes out the barkeep beat, and it earns two things at once: the date on
 * the map becomes the calendar (ENABLE_GOAL), and the trade post opens (ENABLE_SHOP).
 *
 * The shop used to open back at the moment the map was acquired, which put the button on the
 * page before anyone had mentioned a shop — and left the barkeep saying `shopkeeper will be
 * happy to see you` about a post the player could already have walked into. Now his line is the
 * unlock. Load-bearing, the same way the healer's price is for the coin counter: he is the only
 * one who says a shopkeeper exists, so if he stops saying it, nothing does.
 *
 * Both forks run it. The caravan branch is dead today (travelWithCaravan is disabled), but the
 * machine's CanShop state has to reach `enabled` for CityReady, so a live caravan path that
 * skipped this would strand the player short of Depart.
 */
const leaveBarkeep = onDone => {
  const goal = enableGoal(() => {});
  const shop = enableShop(() => {});

  return {
    title: 'OK',
    enabled: () => true,
    execute: context => {
      goal.execute(context);
      shop.execute(context);

      onDone();

      return {};
    }
  };
};

const barkeepAlone = onDone => ({
  title: 'the barkeep',
  description: [
    'he does not ask what is out there. he has decided not to know.',
    `"you won't make it far without a horse and supplies."`,
    `"starvation and thirst will get you. dysentery if they don't."`,
    `"shopkeeper is down the road. she will be glad of you."`,
    `"buy what you need. i don't want trouble in here."`
  ],
  buttons: [leaveBarkeep(onDone)]
});

// TODO:
// eslint-disable-next-line no-unused-vars
const barkeepCaravan = onDone => ({
  title: 'the barkeep',
  description: [
    `barkeep nods approvingly. "a wise choice."`,
    `"best meet your companions at the edge of town"`,
    `"prepare yourself. the journey is long and dangerous"`
  ],
  buttons: [leaveBarkeep(onDone)]
});

// She is greedy and the traveler can see it. That read is his to make — he is a man among
// strangers and judging them keeps him alive. The old `her greedy little eyes brighten` was not
// wrong to reach the verdict, it was wrong to hand it over free and with a sneer in it. He
// watches her look at the map and then at his pockets, and the conclusion is his own.
//
// Her greed does the mechanical work for us. The horse is the dearest thing on the shelf, so a
// woman steering him to it first is both in character and the guidance the scene has to carry,
// and she uses the map to justify the price rather than to admire it.
//
// She is not the traveler, and the tell is not length — a pass that made her voluble to get away
// from him only made her slow. What is his is that he addresses nobody: no questions, no orders,
// no `you`. She is as clipped as he is and still unmistakable, because every line she has is
// pointed at a man across a counter. If a line of hers would sit unchanged in the notification
// log, it is his.
//
// Load-bearing: the no-horse line is the only place the game says a horse comes before anything
// else. There is no tutorial and the buy buttons are all lit at once, so if she stops saying it,
// nothing does. barkeepAlone sets it up one scene earlier — keep the two in step.
//
// Each spoken line gets its own array entry. Event.js splits on /(".*?")/, so a string that
// stitches quote-narration-quote together renders as three spans in one paragraph.
const tradePost = (context, onDone) => {
  const hasHorse = context.party.resources.items.horse.value;

  return {
    title: 'the shopkeeper',
    description: [
      'the woman behind the counter puts down what she is holding.',
      'the map goes on the counter. she looks at it. then at my pockets. she prices me before she says a word.',
      hasHorse
        ? '"horse is bought. now food, water, powder. you will want all three and i am the only one selling."'
        : '"no horse. that is the dear one. we start there."',
      '"a man with a map like that can afford it."'
    ],
    // Each good gets its single and its lot, paired. The bulk button carries the live count it
    // would hand over, so the pair reads as one price ladder rather than eight buttons.
    buttons: [
      buyHorse(context),
      buyAmmo(context),
      buyAmmoBulk(context),
      buyFood(context),
      buyFoodBulk(context),
      buyWater(context),
      buyWaterBulk(context),
      // Optionally include special offers or investment opportunities
      // specialOffer ? specialOfferButton(context) : null,
      // investInShop ? investInShopButton(context) : null,
      leave(onDone)
    ]
  };
};

export { examineMap, firstDrunk, medicineWoman, rancher, tradePost };
